import { Map, List } from 'immutable'
import _isEmpty from 'lodash/isEmpty'
import _get from 'lodash/get'
import _reduce from 'lodash/reduce'
import _parseInt from 'lodash/parseInt'
import { get as getConfig } from 'config'
import {
  FILTER_SET_DEFAULT,
  FILTER_SET_YOGA,
  FILTER_SET_FITNESS,
  FILTER_SET_RECIPE,
  FILTER_SET_ORIGINAL,
  FILTER_SET_CONSCIOUS,
  FILTER_SET_MEDITATION,
  FILTER_SET_FILM,
} from 'services/filter-set'
import {
  DEFAULT_PATH_YOGA,
  DEFAULT_PATH_FILM,
  DEFAULT_PATH_FITNESS,
  DEFAULT_PATH_RECIPE,
  DEFAULT_PATH_ORIGINAL,
  DEFAULT_PATH_CONSCIOUS,
  DEFAULT_PATH_MEDITATION,
} from 'services/url/constants'
import { historyRedirect } from 'services/navigation'
import { isAnonymousWordPressContent } from 'services/url'

const config = getConfig()
const proxyAnonymousPerson = !!_get(config, ['features', 'marketing', 'proxyAnonymousPerson'])

export const URL_CHANGE_TYPE_PATH = 'path'
export const URL_CHANGE_TYPE_QUERY = 'query'

const FILTER_SET_QUERY_PARAM = 'filter-set'

/**
 * @param {List} options - The filter options
 * @param {Object} location - The location object
 * @returns {List} The SEO links data list
 *
 * SEO links are only rendered on the pages with no query filter params
 */
export const getSeoLinks = (options, location) => {
  return _isEmpty(location.query)
    ? options.reduce((acc, option) => {
      if (option.get('path')) {
        const link = Map({
          name: option.get('name'),
          path: option.get('path'),
        })
        return acc.push(link)
      }
      return acc
    }, List())
    : List()
}

/**
 * @param {Object} location - The location object
 * @returns {string} - The path without leading slash
 */
export const getLocationPath = (location) => {
  return location.pathname.substring(1)
}

/**
 * @param {Object} location - The location object
 * @param {string} filterSet - The filter set type
 * @returns {string} - The default path
 */
export function getDefaultPath (location, filterSetType) {
  switch (filterSetType) {
    case FILTER_SET_YOGA:
      return DEFAULT_PATH_YOGA
    case FILTER_SET_FITNESS:
      return DEFAULT_PATH_FITNESS
    case FILTER_SET_RECIPE:
      return DEFAULT_PATH_RECIPE
    case FILTER_SET_ORIGINAL:
      return DEFAULT_PATH_ORIGINAL
    case FILTER_SET_CONSCIOUS:
      return DEFAULT_PATH_CONSCIOUS
    case FILTER_SET_MEDITATION:
      return DEFAULT_PATH_MEDITATION
    case FILTER_SET_FILM:
      return DEFAULT_PATH_FILM
    default:
      return getLocationPath(location)
  }
}

/**
 * Checking if the page path exsists as a filter option
 * @param {List} options - The filter options
 * @param {string} path - The path
 * @returns {boolean}
 */
export const getPathIsInOptions = (options, path) => options.some((option) => option.get('path') === path)

/**
 * @param {List} options - The filter options
 * @param {Object} location - The location object
 * @param {Map} option - The selected option
 * @returns {URL_CHANGE_TYPE_PATH || URL_CHANGE_TYPE_QUERY} - Should we change query params
 * or page path when filter option is applied
 */
export const getUrlChangeType = (location, options, option) => {
  const path = getLocationPath(location)
  const pathInOptions = getPathIsInOptions(options, path)
  const optionDefaultPath = option.get('allOptionsPath')
  if (pathInOptions || path === optionDefaultPath) {
    return URL_CHANGE_TYPE_PATH
  }
  return URL_CHANGE_TYPE_QUERY
}

/**
 * @param {List} options - The filter options
 * @param {Object} location - The location object
 * @param {string} param - The filter's query param
 * @returns {Map} - Selected option
 */
export const getFilterSelectedOption = ({
  options,
  location,
  param,
  defaultValue = null,
}) => {
  const path = getLocationPath(location)
  const value = _get(location, ['query', param], defaultValue)

  // Trying to find te selected option by query param or location path or default value
  let selectedOption = options.find((option) => {
    const optionValue = option.get('value')
      ? option.get('value').toString()
      : null
    return value ? optionValue === value : option.get('path') === path
  })

  // Trying ot find default option if neither query param nor path matches
  if (!selectedOption) {
    selectedOption = options.find((option) => option.get('value') === null)
  }

  return selectedOption
}

/**
 * Changing query params for the filter options which do not have
 * subcategory pages paths
 * @param {Object} params - The params object
 * @param {Map} params.filterOption - The filter option data
 * @param {string} params.param - The filter query param
 * @param {string} params.filterSetType - The current filter set type
 * @param {string} params.language - The user's language
 * @param {Object} params.location - The locatino oblect
 * @param {Object} params.history - The history object
 * @param {Map} params.auth - The auth data
 * @param {boolean} params.isLoggedIn - The value indicating if user is logged in
 */
export const changeLocationQuery = ({
  filterOption,
  param,
  filterSetType,
  language,
  location,
  history,
  auth,
  isLoggedIn,
}) => {
  const value = filterOption.get('value')
  const { pathname, query = {} } = location
  query[param] = value === null ? undefined : value
  // If the pager is in use, remove it as soon as the filters are interacted with
  query.page = undefined
  if (filterSetType !== FILTER_SET_DEFAULT
    && !isAnonymousWordPressContent(pathname, isLoggedIn, proxyAnonymousPerson)) {
    query[FILTER_SET_QUERY_PARAM] = filterSetType
  }
  historyRedirect({
    history, url: pathname, query, auth, language,
  })
}

/**
 * Changing path and/or query params for the filter options which have
 * subcategory pages paths
 * @param {Object} params - The params object
 * @param {Map} params.filterOption - The filter option data
 * @param {string} params.param - The filter query param
 * @param {string} params.changeType - The location change type
 * @param {List} params.filtersList - The current filters data list
 * @param {string} params.filterSetType - The current filter set type
 * @param {string} params.language - The user's language
 * @param {Object} params.location - The location oblect
 * @param {Object} params.history - The history object
 * @param {Map} params.auth - The auth data
 * @param {boolean} params.isLoggedIn - The value indicating if user is logged in
 */
export const changeLocation = ({
  filterOption,
  param,
  changeType,
  filtersList,
  filterSetType,
  language,
  location,
  history,
  auth,
  isLoggedIn,
}) => {
  const query = _get(location, 'query', {})
  const value = filterOption.get('value')

  let path = filterOption.get('path')
  let filterParam = param
  if (value === null) {
    // Find the filter type so we can get options from the filter store.
    // Also keep the query value so we can find the selected filter option.
    // Save these things in the reduction.
    const nextSlugData = _reduce(
      location.query,
      (reduction, queryValue, queryParam) => {
        if (reduction) {
          return reduction
        }
        // From the available filters for this data find the first one
        // where it has a query paramter in the URL.
        const queryFilterSet = filtersList.find(
          (item) => item.get('param') === queryParam,
        )
        // If we found a data filter save the filter type and the query value
        // to do work on the fitler store.
        if (queryFilterSet) {
          return {
            type: queryFilterSet.get('type'),
            queryValue,
            queryParam,
          }
        }
        return reduction
      },
      null,
    )
    // We don't yet have the path for the the next slug so we need to get
    // option that is selected based on the query, then use its path.
    if (nextSlugData) {
      const previousData = filtersList.find(
        (item) => item.get('type') === nextSlugData.type,
      )
      if (previousData) {
        const selectedOption = previousData
          .getIn(['filter', 'options'], List())
          .find(
            (option) => option.get('value') === _parseInt(nextSlugData.queryValue),
          )
        // If we found a selected option use its param and path instead
        // of the filter from the onChange event.
        if (selectedOption) {
          filterParam = nextSlugData.queryParam
          path = selectedOption.get('path')
        }
      }
    }
  }
  if (changeType === URL_CHANGE_TYPE_PATH && path) {
    // Remove the query parameter if is in use
    query[filterParam] = undefined
    if (filterSetType !== FILTER_SET_DEFAULT
      && !isAnonymousWordPressContent(path, isLoggedIn, proxyAnonymousPerson)) {
      query[FILTER_SET_QUERY_PARAM] = filterSetType
    }
    historyRedirect({
      history, url: path, query, auth, language, isLoggedIn,
    })
  } else {
    changeLocationQuery({
      filterOption,
      param,
      filterSetType,
      language,
      location,
      history,
      auth,
      isLoggedIn,
    })
  }
}

/**
 * @param {Object} params - The params object
 * @param {string} params.filterSetType - The current filter set type
 * @param {string} params.language - The user's language
 * @param {Object} params.location - The location oblect
 * @param {Object} params.history - The history object
 * @param {Map} params.auth - The auth data
 */
export const clearFilters = ({
  filterSetType,
  language,
  location,
  history,
  auth,
}) => {
  historyRedirect({
    history,
    url: getDefaultPath(location, filterSetType),
    auth,
    language,
  })
}

/**
 * @param {string} params.filterSetType - The current filter set type
 * @param {Object} params.location - The location oblect
 */
export const getAppliedFiltersCount = (location, filterSetType) => {
  const queryParamsKeys = Object.keys(location.query)
    .filter((p) => !['sort', FILTER_SET_QUERY_PARAM].includes(p))
  const isDefaulLocation = getLocationPath(location) === getDefaultPath(location, filterSetType)
  return isDefaulLocation ? queryParamsKeys.length : queryParamsKeys.length + 1
}
